/**
 * The retrospective phase machine.
 *
 * This module is the single source of truth for phase ordering and for the
 * visibility rules that depend on the phase. The server's redaction layer and
 * the web client both import these helpers, so they can never disagree about
 * whether comments are revealed or whether vote totals are public.
 */

export const RETRO_PHASES = ['COMMENTS', 'GROUP', 'VOTE', 'ACTION_PLAN', 'CLOSED'] as const;

export type RetroPhase = (typeof RETRO_PHASES)[number];

export const PHASE_LABELS: Record<RetroPhase, string> = {
  COMMENTS: 'Comments',
  GROUP: 'Group',
  VOTE: 'Vote',
  ACTION_PLAN: 'Action Plan',
  // The label names the *state* of this final step, which is where it is shown
  // the most — the header step chip and the step picker both read it back once
  // the retro has ended, and "Close" there looked like an unfinished action.
  CLOSED: 'Summary',
};

/** Short helper text shown under the step name in the facilitator's step picker. */
export const PHASE_HINTS: Record<RetroPhase, string> = {
  COMMENTS: 'Everyone writes their ideas privately.',
  GROUP: 'Group similar comments and attach topics.',
  VOTE: 'Prioritise what matters most to the team.',
  ACTION_PLAN: 'Discuss the top comments and commit to actions.',
  CLOSED: 'Review the report and rate the session.',
};

export const TOTAL_STEPS = RETRO_PHASES.length;

export function phaseIndex(phase: RetroPhase): number {
  return RETRO_PHASES.indexOf(phase);
}

/** 1-based step number, as displayed in the UI ("STEP 2 / 5"). */
export function stepNumber(phase: RetroPhase): number {
  return phaseIndex(phase) + 1;
}

export function nextPhase(phase: RetroPhase): RetroPhase | null {
  return RETRO_PHASES[phaseIndex(phase) + 1] ?? null;
}

export function prevPhase(phase: RetroPhase): RetroPhase | null {
  const i = phaseIndex(phase);
  return i <= 0 ? null : (RETRO_PHASES[i - 1] ?? null);
}

export function isAtOrAfter(phase: RetroPhase, target: RetroPhase): boolean {
  return phaseIndex(phase) >= phaseIndex(target);
}

/**
 * Comments written by other participants stay hidden for the whole COMMENTS
 * phase. Moving to GROUP is the "reveal" moment.
 */
export function commentsAreRevealed(phase: RetroPhase): boolean {
  return isAtOrAfter(phase, 'GROUP');
}

/**
 * Aggregate vote counts per comment/group are withheld during voting itself so
 * that nobody can bandwagon onto an already-popular card. They become visible
 * once the team moves on to discussing the results.
 */
export function voteTotalsVisible(phase: RetroPhase): boolean {
  return isAtOrAfter(phase, 'ACTION_PLAN');
}

export function votingIsOpen(phase: RetroPhase): boolean {
  return phase === 'VOTE';
}

/** Whether the facilitator should be asked to confirm before leaving `phase`. */
export function transitionNeedsConfirmation(from: RetroPhase, to: RetroPhase): boolean {
  // Revealing comments and closing the retro are both irreversible in practice.
  if (from === 'COMMENTS' && to === 'GROUP') return true;
  if (to === 'CLOSED') return true;
  return false;
}
